import { HubConnection, HubConnectionBuilder, HubConnectionState, IHttpConnectionOptions, LogLevel } from '@microsoft/signalr';

import { combineQuery } from '../helpers';
import { QueryModel } from '../models';

export class BaseLiveConnection {
    connection:  HubConnection | null = null;

    connectionStartPromise: Promise<any> | null = null;
    connectionStopPromise: Promise<any> | null = null;

    url = '';

    constructor(url: string) {
        this.url = url;
    }

    async waitStarted() {
        if (this.connectionStartPromise instanceof Promise) {
            await this.connectionStartPromise;
            this.connectionStartPromise = null;
        }
    }

    async waitStop() {
        if (this.connectionStopPromise instanceof Promise) {
            await this.connectionStopPromise;
            this.connectionStopPromise = null;
        }
    }

    // `.catch(onRejected)` already forwards the error; only the type hid it.
    async init(options: IHttpConnectionOptions, params?: QueryModel, onRejected?: (error?: unknown) => void) {
        await this.waitStop();
        await this.waitStarted();
        if (this.connection?.state !== HubConnectionState.Connected) {
            this.connection = this.createConnection(options, params);
            this.connectionStartPromise = this.connection.start().catch(onRejected);
            await this.waitStarted();
        }
        return this.connection;
    }

    createConnection(options: IHttpConnectionOptions, params?: QueryModel) {
        // Retry delays: 300ms, 600ms, 2s, 10s, 30s, then stop.
        const retryDelays = [300, 600, 2000, 10000, 30000];
        return new HubConnectionBuilder()
            .withUrl(`${this.url}${combineQuery(params)}`, options)
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect(retryDelays)
            .build();
    }

    protected handleInvokeError(methodName: string, error: Error): void {
        console.error(`[BaseLiveConnection] invoke "${methodName}" failed:`, error);
    }

    async invoke(methodName: string, ...args: any) {
        await this.waitStarted();
        if (this.connection?.state === HubConnectionState.Connected) {
            try {
                return await this.connection.invoke(methodName, ...args);
            } catch (error) {
                this.handleInvokeError(methodName, error as Error);
            }
        }
    }

    async send(methodName: string, ...args: any) {
        await this.waitStarted();
        if (this.connection?.state === HubConnectionState.Connected) return this.connection.send(methodName, ...args);
    }

    on<T>(methodName: string, handler: (args: T) => void) {
        if (this.connection) this.connection.on(methodName, handler);
        return this;
    }

    onReconnecting(callback: (error?: Error) => void) {
        if (callback && this.connection) {
            this.connection.onreconnecting(callback);
        }
    }

    onClose(callback: (error?: Error) => void) {
        // No state guard: onclose can be registered in any state (matches onReconnecting/onReconnected).
        // The old `state === Connected` check meant the handler was never attached when wired around
        // start()/reconnect time, so permanent-disconnect was unobservable.
        if (callback && this.connection) {
            this.connection.onclose(callback);
        }
    }

    onReconnected(callback: (connectionId?: string) => void) {
        if (callback && this.connection) {
            this.connection.onreconnected(callback);
        }
    }

    async stop() {
        await this.waitStarted();
        await this.waitStop();
        if (this.connection?.state === HubConnectionState.Connected) {
            this.connectionStopPromise = this.connection.stop().then(() => {
                this.connectionStartPromise = null;
            });
            await this.waitStop();
        }
    }
}
